import React, { useState } from 'react';
import Button from '../../../../ui/canon/Button.jsx';
import Input from '../../../../ui/canon/Input.jsx';
import SearchBar from '../../../../ui/canon/SearchBar.jsx';
import { CalendarIcon, FunnelIcon, XMarkIcon, WrenchScrewdriverIcon, TrophyIcon } from '@heroicons/react/24/outline';

export default function SprintFilters({ filters, onFiltersChange, sprintCounts, className = '' }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleStatusChange = (status) => {
    onFiltersChange({
      ...filters,
      status: filters.status === status ? undefined : status,
    });
  };

  const handleTypeChange = (sprintType) => {
    onFiltersChange({
      ...filters,
      sprintType: filters.sprintType === sprintType ? undefined : sprintType,
    });
  };

  const handleDateChange = (field, value) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    });
  };

  const handleSearchChange = (value) => {
    onFiltersChange({
      ...filters,
      search: value || undefined,
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({});
  };

  const hasActiveFilters = !!(
    filters.status ||
    filters.sprintType ||
    filters.startDate ||
    filters.endDate ||
    filters.search
  );

  const activeFilterCount = [
    filters.status,
    filters.sprintType,
    filters.startDate,
    filters.endDate,
    filters.search,
  ].filter(Boolean).length;

  const getQuickDateRange = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  };

  return (
    <div className={`bg-succession-bg-primary border-b border-succession-border ${className}`}>
      {/* Quick Filters Bar */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <h3 className="text-sm font-medium text-succession-text-primary">Filter Sprints</h3>
            {hasActiveFilters && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-succession-text-secondary">
                  {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-xs text-succession-text-secondary hover:text-succession-text-primary"
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-succession-text-secondary hover:text-succession-text-primary"
          >
            <FunnelIcon className="w-4 h-4 mr-2" />
            {isExpanded ? 'Less' : 'More'} Filters
          </Button>
        </div>

        {/* Status Filter Pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            onClick={() => handleStatusChange(undefined)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              !filters.status
                ? 'bg-succession-text-primary text-succession-bg-primary border-succession-text-primary'
                : 'bg-succession-bg-primary text-succession-text-primary border-succession-border hover:border-succession-border-hover'
            }`}
          >
            All Sprints ({sprintCounts.total || 0})
          </button>
          {[
            { value: 'planned', label: 'Planned', count: sprintCounts.planned || 0 },
            { value: 'active', label: 'Active', count: sprintCounts.active || 0 },
            { value: 'closed', label: 'Closed', count: sprintCounts.closed || 0 },
            { value: 'abandoned', label: 'Abandoned', count: sprintCounts.abandoned || 0 },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleStatusChange(option.value)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                filters.status === option.value
                  ? 'bg-succession-primary text-white border-succession-primary'
                  : 'bg-succession-bg-primary text-succession-text-primary border-succession-border hover:border-succession-border-hover'
              }`}
            >
              {option.label} ({option.count})
            </button>
          ))}
        </div>

        {/* Type Filter Pills */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm font-medium text-succession-text-secondary self-center mr-2">Type:</span>
          {[
            { value: 'diy', label: 'DIY', icon: WrenchScrewdriverIcon, count: sprintCounts.diy || 0 },
            { value: 'coach_led', label: 'Coach-Led', icon: TrophyIcon, count: sprintCounts.coach_led || 0 },
          ].map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => handleTypeChange(option.value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  filters.sprintType === option.value
                    ? 'bg-succession-primary text-white border-succession-primary'
                    : 'bg-succession-bg-primary text-succession-text-primary border-succession-border hover:border-succession-border-hover'
                }`}
              >
                <Icon className="w-4 h-4" />
                {option.label} ({option.count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-succession-border bg-succession-bg-secondary">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
            {/* Search */}
            <div className="space-y-2">
              <label htmlFor="search" className="block text-sm font-medium text-succession-text-primary">
                Search Sprints
              </label>
              <div className="relative">
                <SearchBar
                  value={filters.search || ''}
                  onChange={handleSearchChange}
                  placeholder="Search by name or objective..."
                  variant="outline"
                  size="md"
                  className={filters.search ? 'pr-8' : ''}
                />
                {filters.search && (
                  <button
                    onClick={() => handleSearchChange('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-succession-text-muted hover:text-succession-text-primary z-10"
                    type="button"
                    aria-label="Clear search"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <label htmlFor="startDate" className="block text-sm font-medium text-succession-text-primary">
                Start Date From
              </label>
              <div className="relative">
                <Input
                  id="startDate"
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                  className="pr-10"
                />
                <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-succession-text-muted pointer-events-none" />
              </div>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <label htmlFor="endDate" className="block text-sm font-medium text-succession-text-primary">
                End Date Until
              </label>
              <div className="relative">
                <Input
                  id="endDate"
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => handleDateChange('endDate', e.target.value)}
                  className="pr-10"
                />
                <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-succession-text-muted pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Quick Date Presets */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-succession-text-primary mb-2">
              Quick Date Ranges
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'This Quarter', days: 90 },
                { label: 'Last 6 Months', days: 180 },
                { label: 'This Year', days: 365 },
              ].map((preset) => {
                const range = getQuickDateRange(preset.days);
                return (
                  <Button
                    key={preset.label}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onFiltersChange({
                        ...filters,
                        startDate: range.startDate,
                        endDate: range.endDate,
                      });
                    }}
                    className="text-xs"
                  >
                    {preset.label}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onFiltersChange({
                    ...filters,
                    startDate: undefined,
                    endDate: undefined,
                  });
                }}
                className="text-xs"
              >
                Clear Dates
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

